/**
 * Constellation View — Cytoscape concentric ring visualization.
 *
 * Generic hub-and-spoke constellation renderer (DRR-085, DRR-126).
 * Ring labels and colors come from ConstellationTemplateRing metadata passed
 * via `ringMeta` — NO hardcoded industry-specific values.
 *
 * Works for any constellation: ALF care service plan, legal case plan,
 * financial account plan, etc. The backend config defines what each ring means.
 *
 * Data-driven pattern (DRR-055, DRR-126):
 * - Ring labels/colors from ConstellationTemplateRing.label / .color
 * - Definition nodes from ConstellationTemplateRing.definitionNodes
 * - NEVER assumes industry-specific ring semantics in frontend code
 *
 * CareTeamConstellationView is a backward-compatible alias for existing importers.
 */

import { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { Stack, Paper, Group, Text, Badge, Select, ActionIcon, Tooltip, Anchor, Divider } from '@mantine/core';
import { IconRefresh, IconMaximize, IconUser, IconUsers, IconExternalLink } from '@tabler/icons-react';
import cytoscape, { type Core, type NodeSingular } from 'cytoscape';
import type { InstanceStatusSummary } from './hooks/useConstellationTaskSummary';
import type { CoResponder } from './hooks/useCoRespondersForInstance';

// ============================================================================
// Types — from constellation seed data
// ============================================================================

export interface ConstellationNode {
  nodeId: string;
  name: string;
  role: string;
  ring: number; // 0=center, 1-4=rings defined by backend constellation template
  metadata?: Record<string, unknown>;
}

export interface ConstellationEdge {
  sourceId: string;
  targetId: string;
  edgeType: string; // COORDINATED_BY | SERVES | PERFORMED_BY | REGULATED_BY | etc.
  label?: string;
}

/**
 * Backend-defined ring display config from ConstellationTemplateRing.
 * When provided, overrides hardcoded fallback ring labels/colors.
 * Distinct from usePrimitiveConstellation's ConstellationViewRingConfig (field graph).
 */
export interface ConstellationViewRingConfig {
  ringNumber: number;
  label: string;
  color?: string | null;
}

/** DRR-126: Definition mode node from ConstellationTemplateRing.definitionNodes */
export interface ConstellationDefinitionNode {
  taskGroupRef: string;
  workflowRef: string;
  department?: string | null;
  ownerRole?: string | null;
  label?: string | null;
  description?: string | null;
}

export interface ConstellationViewProps {
  /** Center node — the subject (resident, client, project, etc.) */
  center: ConstellationNode;
  /** All nodes in the constellation */
  nodes: ConstellationNode[];
  /** Edges connecting nodes */
  edges: ConstellationEdge[];
  /** Currently selected node ID */
  selectedNodeId?: string | null;
  /** Callback when a node is clicked */
  onNodeSelect?: (nodeId: string) => void;
  /** Callback to recenter on a different node */
  onRecenter?: (nodeId: string) => void;
  /** Height of the container */
  height?: number;
  /**
   * DRR-126: Constellation mode. In 'definition' mode Ring 3 renders
   * definitionNodes (task group → workflow mapping) instead of live instances.
   */
  mode?: 'definition' | 'instance';
  /**
   * DRR-126: Ring 3 definition nodes from GetConstellationTemplate query.
   * Only used when mode === 'definition'.
   */
  definitionNodes?: ConstellationDefinitionNode[];
  /**
   * DRR-126: Callback when user clicks a workflow link in definition mode Ring 3.
   * Typically navigates to WorkflowDetailPage for that wf-*.
   */
  onWorkflowClick?: (workflowRef: string) => void;
  /**
   * DRR-126: Backend-defined ring metadata from ConstellationTemplateRing.
   * Provides data-driven ring labels and colors — avoids hardcoded industry values.
   * When provided, overrides the default fallback ring labels/colors.
   */
  ringMeta?: ConstellationViewRingConfig[];
  /**
   * DRR-126 Phase 2: Task status summary keyed by workflow instance ID.
   * Drives Ring 3 status overlays in instance mode — border color reflects
   * aggregate task status (completed=green, in_progress=blue, overdue=red, pending=gray).
   * Obtain via useConstellationTaskSummary().byInstance.
   */
  taskSummaryByInstance?: Map<string, InstanceStatusSummary>;
  /**
   * DRR-126 Phase 3: Co-responder data for the currently selected Ring 3 instance.
   * When a Ring 3 node is selected in instance mode, pass the co-responders from
   * useCoRespondersForInstance() to surface peer actor task status in the detail panel.
   */
  coRespondersForSelectedInstance?: CoResponder[];
  /**
   * DRR-126 Phase 3: Callback when user selects a Ring 3 node (workflow instance).
   * Caller uses this to trigger useCoRespondersForInstance for that instanceId.
   */
  onInstanceSelect?: (instanceId: string) => void;
}

// Ring → concentric level value (higher = closer to center)
const RING_LEVELS: Record<number, number> = {
  0: 100,  // center
  1: 80,   // clinical staff
  2: 55,   // authorities
  3: 35,   // deliverables
  4: 20,   // regulatory
};

// Fallback ring labels (used when ringMeta not provided by caller)
// These are ALF-specific defaults — callers SHOULD pass ringMeta from backend template
const FALLBACK_RING_LABELS: Record<number, string> = {
  0: 'Center',
  1: 'Ring 1',
  2: 'Ring 2',
  3: 'Ring 3',
  4: 'Ring 4',
};

// Role → node shape
const ROLE_SHAPES: Record<string, string> = {
  center: 'round-rectangle',
  nurse: 'ellipse',
  cna: 'ellipse',
  med_aide: 'ellipse',
  don: 'diamond',
  coordinator: 'hexagon',
  payer: 'ellipse',
  agency: 'hexagon',
  deliverable: 'round-rectangle',
  regulatory: 'round-rectangle',
};

// Fallback ring colors (used when ringMeta not provided)
const FALLBACK_RING_COLORS: Record<number, string> = {
  0: '#8b5cf6',  // purple — center
  1: '#3b82f6',  // blue — ring 1
  2: '#10b981',  // green — ring 2
  3: '#f59e0b',  // amber — ring 3
  4: '#ef4444',  // red — ring 4
};

// Edge type → color
const EDGE_COLORS: Record<string, string> = {
  COORDINATED_BY: '#3b82f6',
  SERVES: '#10b981',
  PERFORMED_BY: '#f59e0b',
  REGULATED_BY: '#ef4444',
  GOVERNED_BY: '#ef4444',
  ASSIGNED_TO: '#6b7280',
  CONTAINS: '#6b7280',
};

// Cytoscape stylesheet (cast to any — matches UniversalConstellationGraph pattern)
const CARE_TEAM_STYLES = [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': '11px',
      'font-weight': 'normal',
      'text-wrap': 'wrap',
      'text-max-width': '80px',
      width: 70,
      height: 50,
      'border-width': 2,
      'border-color': 'data(borderColor)',
      'background-color': 'data(bgColor)',
      color: '#fff',
    },
  },
  {
    selector: 'node[ring = 0]',
    style: {
      width: 100,
      height: 70,
      'font-size': '13px',
      'font-weight': 'bold',
      'border-width': 3,
    },
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 4,
      'border-color': '#f59e0b',
      'box-shadow': '0 0 10px rgba(245, 158, 11, 0.6)',
    },
  },
  {
    // DRR-126 Phase 2: Ring 3 nodes with task status get thicker status border
    selector: 'node[aggregateStatus != ""]',
    style: {
      'border-width': 4,
    },
  },
  {
    selector: 'edge',
    style: {
      'curve-style': 'bezier',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 0.8,
      width: 1.5,
      'line-color': 'data(color)',
      'target-arrow-color': 'data(color)',
      label: 'data(label)',
      'font-size': '9px',
      color: '#6b7280',
      'text-background-color': '#fff',
      'text-background-opacity': 0.8,
      'text-background-padding': '2px',
    },
  },
];

/** Department → display color for definition mode Ring 3 nodes */
const DEPT_COLORS: Record<string, string> = {
  clinical:      '#3b82f6',  // blue
  direct_care:   '#10b981',  // green
  administrative:'#8b5cf6',  // purple
  dietary:       '#f97316',  // orange
  housekeeping:  '#64748b',  // slate
  default:       '#f59e0b',  // amber (fallback = existing ring 3 color)
};

/** DRR-126 Phase 2: Task aggregate status → Ring 3 border color */
const STATUS_BORDER_COLORS: Record<string, string> = {
  completed:   '#10b981',  // green
  in_progress: '#3b82f6',  // blue
  overdue:     '#ef4444',  // red
  pending:     '#9ca3af',  // gray
};

/**
 * Care Team Constellation — Cytoscape concentric ring visualization.
 *
 * Centers on a resident and shows the full care team with ring-based layout.
 * Uses cytoscape.js directly (same pattern as UniversalConstellationGraph).
 *
 * DRR-126: In 'definition' mode, Ring 3 renders definitionNodes (task group →
 * workflow mapping per department). In 'instance' mode, Ring 3 renders live
 * deliverables (task instances). Clicking a definition-mode Ring 3 node calls
 * onWorkflowClick(workflowRef) to navigate to WorkflowDetailPage.
 */
export function ConstellationView({
  center,
  nodes,
  edges,
  selectedNodeId,
  onNodeSelect,
  onRecenter,
  height = 500,
  mode = 'instance',
  definitionNodes = [],
  onWorkflowClick,
  ringMeta = [],
  taskSummaryByInstance,
  coRespondersForSelectedInstance,
  onInstanceSelect,
}: ConstellationViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [selectedDefNode, setSelectedDefNode] = useState<ConstellationDefinitionNode | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  // DRR-126: Use backend-defined ring metadata when available; fall back to defaults
  const ringLabels: Record<number, string> = ringMeta.length > 0
    ? Object.fromEntries(ringMeta.map((r) => [r.ringNumber, r.label]))
    : FALLBACK_RING_LABELS;
  const ringColors: Record<number, string> = ringMeta.length > 0
    ? Object.fromEntries(ringMeta.map((r) => [r.ringNumber, r.color ?? FALLBACK_RING_COLORS[r.ringNumber] ?? '#6b7280']))
    : FALLBACK_RING_COLORS;

  // Build Cytoscape elements from props
  const elements = useMemo(() => {
    let baseNodes = nodes;

    // DRR-126: In definition mode, replace Ring 3 with definition nodes
    if (mode === 'definition' && definitionNodes.length > 0) {
      baseNodes = [
        ...nodes.filter((n) => n.ring !== 3),
        ...definitionNodes.map((dn) => ({
          nodeId: dn.taskGroupRef,
          name: dn.label ?? dn.taskGroupRef,
          role: 'deliverable',
          ring: 3,
          metadata: {
            taskGroupRef: dn.taskGroupRef,
            workflowRef: dn.workflowRef,
            department: dn.department ?? 'default',
            ownerRole: dn.ownerRole,
            description: dn.description,
            isDefinitionNode: true,
          } as Record<string, unknown>,
        })),
      ];
    }

    const cyNodes = baseNodes.map((node) => {
      const dept = (node.metadata?.department as string) ?? 'default';
      const isDefNode = Boolean(node.metadata?.isDefinitionNode);
      const bgColor = isDefNode
        ? (DEPT_COLORS[dept] ?? DEPT_COLORS.default)
        : (ringColors[node.ring] ?? '#6b7280');

      // DRR-126 Phase 2: Ring 3 status overlay in instance mode
      const instanceId = (node.metadata?.instanceId as string) ?? node.nodeId;
      const taskStatus = (mode === 'instance' && node.ring === 3 && taskSummaryByInstance)
        ? taskSummaryByInstance.get(instanceId)
        : undefined;
      const aggregateStatus = taskStatus?.aggregateStatus ?? '';
      const statusBorderColor = aggregateStatus
        ? (STATUS_BORDER_COLORS[aggregateStatus] ?? bgColor)
        : (node.nodeId === center.nodeId ? '#f59e0b' : bgColor);

      return {
        data: {
          id: node.nodeId,
          label: node.name,
          ring: node.ring,
          role: node.role,
          bgColor,
          borderColor: statusBorderColor,
          concentric: RING_LEVELS[node.ring] ?? 10,
          isDefinitionNode: isDefNode,
          workflowRef: (node.metadata?.workflowRef as string) ?? '',
          taskGroupRef: (node.metadata?.taskGroupRef as string) ?? '',
          department: dept,
          ownerRole: (node.metadata?.ownerRole as string) ?? '',
          aggregateStatus,
          hasTaskStatus: Boolean(taskStatus),
        },
      };
    });

    const cyEdges = edges
      .filter((e) => {
        // In definition mode, skip edges to/from removed instance Ring 3 nodes
        if (mode === 'definition' && definitionNodes.length > 0) {
          const definitionNodeIds = new Set(definitionNodes.map((d) => d.taskGroupRef));
          const removedIds = nodes.filter((n) => n.ring === 3).map((n) => n.nodeId);
          const removedSet = new Set(removedIds.filter((id) => !definitionNodeIds.has(id)));
          if (removedSet.has(e.sourceId) || removedSet.has(e.targetId)) return false;
        }
        return true;
      })
      .map((edge, i) => ({
        data: {
          id: `edge-${i}-${edge.sourceId}-${edge.targetId}`,
          source: edge.sourceId,
          target: edge.targetId,
          label: edge.label ?? edge.edgeType.replace(/_/g, ' ').toLowerCase(),
          color: EDGE_COLORS[edge.edgeType] ?? '#6b7280',
        },
      }));

    // In definition mode, add GENERATES edges from center to definition nodes
    if (mode === 'definition' && definitionNodes.length > 0) {
      definitionNodes.forEach((dn, i) => {
        cyEdges.push({
          data: {
            id: `def-edge-${i}-${dn.taskGroupRef}`,
            source: center.nodeId,
            target: dn.taskGroupRef,
            label: 'generates',
            color: DEPT_COLORS[dn.department ?? 'default'] ?? DEPT_COLORS['default'] ?? '#f59e0b',
          },
        });
      });
    }

    return [...cyNodes, ...cyEdges];
  }, [nodes, edges, center, mode, definitionNodes, taskSummaryByInstance, ringColors]);

  // Initialize and update Cytoscape
  useEffect(() => {
    if (!containerRef.current) return () => {};

    // Destroy previous instance
    if (cyRef.current) {
      cyRef.current.destroy();
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: CARE_TEAM_STYLES as any,
      layout: {
        name: 'concentric',
        concentric: (node: NodeSingular) => node.data('concentric') ?? 10,
        levelWidth: () => 1,
        minNodeSpacing: 50,
        padding: 40,
      } as any,
      minZoom: 0.2,
      maxZoom: 3,
      wheelSensitivity: 1,
    });

    // Click handler
    cy.on('tap', 'node', (evt) => {
      const node = evt.target as NodeSingular;
      const nodeId = node.id();
      if (onNodeSelect) onNodeSelect(nodeId);

      // DRR-126: In definition mode, show detail panel for Ring 3 nodes
      if (mode === 'definition' && node.data('isDefinitionNode')) {
        const dn = definitionNodes.find((d) => d.taskGroupRef === nodeId);
        setSelectedDefNode(dn ?? null);
        setSelectedInstanceId(null);
      } else if (mode === 'instance' && node.data('ring') === 3) {
        // DRR-126 Phase 3: Ring 3 node selected in instance mode — surface co-responders
        setSelectedInstanceId(nodeId);
        setSelectedDefNode(null);
        if (onInstanceSelect) onInstanceSelect(nodeId);
      } else {
        setSelectedDefNode(null);
        setSelectedInstanceId(null);
      }
    });

    // Double-tap to recenter
    cy.on('dbltap', 'node', (evt) => {
      const nodeId = evt.target.id();
      if (onRecenter) onRecenter(nodeId);
    });

    cyRef.current = cy;

    return () => {
      cy.destroy();
    };
  }, [elements, onNodeSelect, onRecenter, mode, definitionNodes]);

  // Update selection highlight
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().unselect();
    if (selectedNodeId) {
      cy.getElementById(selectedNodeId).select();
    }
  }, [selectedNodeId]);

  const handleFitView = useCallback(() => {
    cyRef.current?.fit(undefined, 40);
  }, []);

  return (
    <Stack gap="sm">
      {/* Toolbar */}
      <Paper p="xs" withBorder>
        <Group justify="space-between">
          <Group gap="xs">
            <IconUsers size={16} />
            <Text size="sm" fw={500}>
              {mode === 'definition' ? 'Village Operating Manual' : center.name}
            </Text>
            {mode === 'definition' ? (
              <Badge size="sm" variant="light" color="teal">Definition Mode</Badge>
            ) : (
              <>
                <Badge size="sm" variant="light" color="violet">
                  {nodes.filter((n) => n.ring === 1).length} staff
                </Badge>
                <Badge size="sm" variant="light" color="green">
                  {nodes.filter((n) => n.ring === 2).length} authorities
                </Badge>
                {taskSummaryByInstance && taskSummaryByInstance.size > 0 && (() => {
                  let completed = 0, inProgress = 0, overdue = 0, pending = 0;
                  taskSummaryByInstance.forEach((s) => {
                    if (s.aggregateStatus === 'completed') completed++;
                    else if (s.aggregateStatus === 'in_progress') inProgress++;
                    else if (s.aggregateStatus === 'overdue') overdue++;
                    else pending++;
                  });
                  return (
                    <>
                      {overdue > 0 && <Badge size="sm" variant="light" color="red">{overdue} overdue</Badge>}
                      {inProgress > 0 && <Badge size="sm" variant="light" color="blue">{inProgress} active</Badge>}
                      {completed > 0 && <Badge size="sm" variant="light" color="teal">{completed} done</Badge>}
                      {pending > 0 && <Badge size="sm" variant="light" color="gray">{pending} pending</Badge>}
                    </>
                  );
                })()}
              </>
            )}
          </Group>
          <Tooltip label="Fit to view">
            <ActionIcon variant="subtle" onClick={handleFitView}>
              <IconMaximize size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Paper>

      {/* Cytoscape container */}
      <div
        ref={containerRef}
        style={{
          height,
          width: '100%',
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-sm)',
          backgroundColor: '#fafafa',
        }}
      />

      {/* DRR-126: Definition mode — selected Ring 3 node detail */}
      {mode === 'definition' && selectedDefNode && (
        <Paper p="sm" withBorder style={{ borderColor: DEPT_COLORS[selectedDefNode.department ?? 'default'] }}>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={600}>{selectedDefNode.label ?? selectedDefNode.taskGroupRef}</Text>
              {selectedDefNode.department && (
                <Badge size="xs" color={DEPT_COLORS[selectedDefNode.department] ? undefined : 'gray'}
                  style={{ backgroundColor: DEPT_COLORS[selectedDefNode.department ?? 'default'] }}>
                  {selectedDefNode.department.replace(/_/g, ' ')}
                </Badge>
              )}
              {selectedDefNode.ownerRole && (
                <Badge size="xs" variant="light" color="gray">{selectedDefNode.ownerRole}</Badge>
              )}
            </Group>

            {selectedDefNode.description && (
              <Text size="xs" c="dimmed">{selectedDefNode.description}</Text>
            )}

            <Divider />

            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">Task group:</Text>
              <Badge size="xs" variant="outline" color="gray">{selectedDefNode.taskGroupRef}</Badge>
            </Group>

            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">Workflow config:</Text>
              {onWorkflowClick ? (
                <Anchor
                  size="xs"
                  fw={500}
                  onClick={() => onWorkflowClick(selectedDefNode.workflowRef)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {selectedDefNode.workflowRef}
                  <IconExternalLink size={11} />
                </Anchor>
              ) : (
                <Badge size="xs" variant="filled" color="blue">{selectedDefNode.workflowRef}</Badge>
              )}
            </Group>
          </Stack>
        </Paper>
      )}

      {/* DRR-126 Phase 3: Instance mode — co-responder panel for selected Ring 3 node */}
      {mode === 'instance' && selectedInstanceId && (
        <Paper p="sm" withBorder style={{ borderColor: '#6b7280' }}>
          <Stack gap="xs">
            <Group gap="xs">
              <IconUsers size={14} />
              <Text size="sm" fw={600}>Co-responders</Text>
              <Badge size="xs" variant="outline" color="gray">{selectedInstanceId}</Badge>
            </Group>
            {(!coRespondersForSelectedInstance || coRespondersForSelectedInstance.length === 0) ? (
              <Text size="xs" c="dimmed">No co-responder data. Pass coRespondersForSelectedInstance prop.</Text>
            ) : (
              <Stack gap={4}>
                {coRespondersForSelectedInstance.map((r) => (
                  <Group key={r.actorId} gap="xs" justify="space-between">
                    <Group gap={4}>
                      <IconUser size={12} />
                      <Text size="xs" fw={r.isSelf ? 600 : 400}>
                        {r.actorId}{r.isSelf ? ' (you)' : ''}
                      </Text>
                      <Text size="xs" c="dimmed">{r.taskName}</Text>
                    </Group>
                    <Badge
                      size="xs"
                      color={
                        r.status === 'overdue' ? 'red' :
                        r.status === 'in_progress' ? 'blue' :
                        r.status === 'completed' ? 'teal' : 'gray'
                      }
                    >
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {/* Legend */}
      <Paper p="xs" withBorder>
        <Group gap="lg">
          <Text size="sm" fw={500}>Rings:</Text>
          {Object.entries(ringLabels).map(([ring, label]) => (
            <Group key={ring} gap={4}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: ring === '0' ? 3 : '50%',
                  backgroundColor: ringColors[Number(ring)] ?? '#6b7280',
                }}
              />
              <Text size="xs">{label}</Text>
            </Group>
          ))}
          {mode === 'definition' && (
            <>
              <Divider orientation="vertical" />
              <Text size="xs" c="dimmed">Ring 3 colors = department</Text>
            </>
          )}
          {mode === 'instance' && taskSummaryByInstance && taskSummaryByInstance.size > 0 && (
            <>
              <Divider orientation="vertical" />
              <Text size="xs" c="dimmed">Ring 3 border:</Text>
              {Object.entries(STATUS_BORDER_COLORS).map(([status, color]) => (
                <Group key={status} gap={4}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, border: `3px solid ${color}`, backgroundColor: 'transparent' }} />
                  <Text size="xs">{status.replace('_', ' ')}</Text>
                </Group>
              ))}
            </>
          )}
        </Group>
      </Paper>
    </Stack>
  );
}

// Backward-compatible aliases — use ConstellationView/ConstellationNode/ConstellationEdge
export { ConstellationView as CareTeamConstellationView };
export type { ConstellationNode as CareTeamNode };
export type { ConstellationEdge as CareTeamEdge };
export type { ConstellationViewProps as CareTeamConstellationViewProps };
